import dayjs, { Dayjs } from "dayjs";
import React from "react";

// ----- Shared Types -----
export interface NewItem {
  itemName: string;
  quantity: number | string;
  unitPrice: number | string;
  totalPrice: number | string;
}

export interface CategoryDetails {
  type_id?: string | number;
  serv_id?: string | number;
  quantity?: number;
  price?: number;
}

export interface ItemEntry {
  selectedCategory?: string | null;
  // dynamic subcategory keys map to CategoryDetails
  [subCategory: string]: any;
}

export type ItemsState = Record<string, Record<string, ItemEntry>>;

export type OrderData = Record<
  string,
  Record<
    string,
    {
      item_url?: string;
      item_name?: string;
      categories: Record<string, CategoryDetails>;
    }
  >
>;

export interface UserDetails {
  name: string;
  phone: string;
  address: string;
  deliveryDate: Dayjs;
}

// ----- Item Management -----
export const handleAddItem = (
  setNewItems: React.Dispatch<React.SetStateAction<NewItem[]>>
): void => {
  setNewItems((prev) => [...prev, { itemName: "", quantity: 0, unitPrice: 0, totalPrice: 0 }]);
};

export const handleRemoveItem = (
  setNewItems: React.Dispatch<React.SetStateAction<NewItem[]>>,
  index: number
): void => {
  setNewItems((prev) => prev.filter((_, i) => i !== index));
};

export const handleNewItemChange = (
  setNewItems: React.Dispatch<React.SetStateAction<NewItem[]>>,
  index: number,
  field: keyof NewItem,
  value: string | number
): void => {
  setNewItems((prev) => {
    const clone = [...prev];
    if (field === "quantity" || field === "totalPrice") {
      clone[index][field] = value;
    } else {
      clone[index][field] = String(value);
    }
    return clone;
  });
};

// ----- Quantity & Price Control -----
export const handleIncrement = (
  setItems: React.Dispatch<React.SetStateAction<ItemsState>>,
  category_id: string,
  item_id: string,
  subCategory: string,
  step: number = 1
): void => {
  setItems((prev) => {
    const current =
      prev[category_id]?.[item_id]?.[subCategory]?.quantity ?? 0;
    return {
      ...prev,
      [category_id]: {
        ...prev[category_id],
        [item_id]: {
          ...prev[category_id]?.[item_id],
          [subCategory]: {
            ...prev[category_id]?.[item_id]?.[subCategory],
            quantity: parseFloat((current + step).toFixed(2)),
          },
        },
      },
    };
  });
};

export const handleDecrement = (
  setItems: React.Dispatch<React.SetStateAction<ItemsState>>,
  category_id: string,
  item_id: string,
  subCategory: string,
  step: number = 1
): void => {
  setItems((prev) => {
    const current =
      prev[category_id]?.[item_id]?.[subCategory]?.quantity ?? 0;
    return {
      ...prev,
      [category_id]: {
        ...prev[category_id],
        [item_id]: {
          ...prev[category_id]?.[item_id],
          [subCategory]: {
            ...prev[category_id]?.[item_id]?.[subCategory],
            quantity: Math.max(parseFloat((current - step).toFixed(2)), 0),
          },
        },
      },
    };
  });
};

export const handlePriceChange = (
  setItems: React.Dispatch<React.SetStateAction<ItemsState>>,
  category_id: string,
  item_id: string,
  subCategory: string,
  newPrice: string
): void => {
  const priceValue = parseFloat(newPrice);
  setItems((prev) => ({
    ...prev,
    [category_id]: {
      ...prev[category_id],
      [item_id]: {
        ...prev[category_id]?.[item_id],
        [subCategory]: {
          ...prev[category_id]?.[item_id]?.[subCategory],
          price: isNaN(priceValue) ? 0 : priceValue,
        },
      },
    },
  }));
};

// ----- Category Selection -----
export const handleCategorySelect = (
  setItems: React.Dispatch<React.SetStateAction<ItemsState>>,
  category_id: string,
  item_id: string,
  selectedCategory: string,
  order_data: OrderData
): void => {
  const categoryDetails =
    order_data[category_id]?.[item_id]?.categories?.[selectedCategory];
  if (!categoryDetails) return;

  setItems((prev) => ({
    ...prev,
    [category_id]: {
      ...prev[category_id],
      [item_id]: {
        ...prev[category_id]?.[item_id],
        selectedCategory,
        price: categoryDetails.price,
        serv_id: categoryDetails.serv_id,
        type_id: categoryDetails.type_id,
      },
    },
  }));
};

// ----- Quantity Direct Change -----
export const handleQuantityChange = (
  setItems: React.Dispatch<React.SetStateAction<ItemsState>>,
  category_id: string,
  item_id: string,
  subCategory: string,
  newQuantity: number
): void => {
  setItems((prev) => ({
    ...prev,
    [category_id]: {
      ...prev[category_id],
      [item_id]: {
        ...prev[category_id]?.[item_id],
        [subCategory]: {
          ...prev[category_id]?.[item_id]?.[subCategory],
          quantity: Math.max(newQuantity, 0),
        },
      },
    },
  }));
};

// ----- User Details -----
export const handleUserDetailsChange = (
  setUserDetails: React.Dispatch<React.SetStateAction<UserDetails>>,
  event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
): void => {
  const { name, value } = event.target;
  setUserDetails((prev) => ({
    ...prev,
    [name]: value,
  }));
};

export const handleDateChange = (
  setUserDetails: React.Dispatch<React.SetStateAction<UserDetails>>,
  newValue: Dayjs | null
): void => {
  if (newValue && dayjs.isDayjs(newValue)) {
    setUserDetails((prev) => ({
      ...prev,
      deliveryDate: newValue,
    }));
  }
};

